import { useState } from 'react';
import { Maximize2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { PAIRING_SCREENSHOT_URL } from './appleTvSetup';

/**
 * What the user will be looking at on the wall.
 *
 * A real photograph when one is supplied; until then a drawing of the same
 * layout. Drawn rather than a grey placeholder because the point of this panel
 * is recognition — the user has to match what is on the TV to what is on the
 * page, and a box saying "screenshot pending" helps nobody.
 *
 * The code shown is deliberately `0 0 0 0 0 0` and the QR is a flat block: an
 * invented-looking code cannot be mistaken for one to type in, and a fake
 * scannable QR would be worse than none.
 */
export function PairingScreenPreview() {
  if (PAIRING_SCREENSHOT_URL) {
    return <PairingScreenshot src={PAIRING_SCREENSHOT_URL} />;
  }

  return (
    <figure className="w-full">
      {/* 16:9, the shape of the television it represents. */}
      <div
        aria-hidden
        className="relative aspect-video w-full overflow-hidden rounded-xl border border-slate-700 bg-slate-900 p-4 text-white"
      >
        <div className="flex h-full flex-col items-center justify-center gap-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            ProsperaSign
          </p>

          <div className="flex items-center gap-4">
            {/* Stand-in for the QR block, not a scannable code. */}
            {/* Flex-wrap rather than a grid: this is a fixed 4x4 decoration, and
                the project's grid rule is about responsive page layout. */}
            <div className="flex h-14 w-14 shrink-0 flex-wrap rounded bg-white p-1">
              {Array.from({ length: 16 }).map((_, i) => (
                <span
                  key={i}
                  className={`h-1/4 w-1/4 ${
                    [0, 1, 2, 4, 6, 8, 9, 11, 13, 15].includes(i) ? 'bg-slate-900' : 'bg-white'
                  }`}
                />
              ))}
            </div>

            <div className="text-left">
              <p className="text-[9px] uppercase tracking-wider text-slate-400">Pairing code</p>
              <p className="font-mono text-2xl font-bold tracking-[0.2em] text-white">000000</p>
            </div>
          </div>

          <ol className="mt-1 space-y-0.5 text-center text-[9px] leading-tight text-slate-400">
            <li>1 · Open your ProsperaSign dashboard on the web</li>
            <li>2 · Scan the QR code, or go to Screens → Pair Device</li>
            <li>3 · Enter the 6-digit code</li>
          </ol>
        </div>
      </div>

      <figcaption className="mt-2 text-xs text-slate-500">
        What the Apple TV shows once the app is open. Your code will be six digits.
      </figcaption>
    </figure>
  );
}

/**
 * The screenshot, with a way to see it properly.
 *
 * It sits in a 300px column, which is enough to recognise the screen but not to
 * read the six digits or the buttons on it — and recognition is the whole point,
 * so the detail has to be reachable. Click opens it at full width.
 *
 * A <button> rather than a click handler on the image: this is a real control,
 * and it has to be reachable by keyboard and announced as one.
 */
function PairingScreenshot({ src }: { src: string }) {
  const [zoomed, setZoomed] = useState(false);
  const alt =
    'The ProsperaSign pairing screen on an Apple TV, showing a QR code and a six-digit code';

  return (
    <figure className="w-full">
      <button
        type="button"
        onClick={() => setZoomed(true)}
        className="group relative block w-full cursor-zoom-in overflow-hidden rounded-xl border border-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label="Enlarge the pairing screen image"
      >
        <img src={src} alt={alt} className="w-full" />
        {/* Only on hover/focus: a permanent badge over a small image is more
            clutter than affordance. */}
        <span className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
          <Maximize2 className="h-3.5 w-3.5" />
        </span>
      </button>

      <figcaption className="mt-2 text-xs text-slate-500">
        What the Apple TV shows once the app is open. Your code will be six digits.{' '}
        <button
          type="button"
          onClick={() => setZoomed(true)}
          className="font-medium text-primary hover:underline"
        >
          Enlarge
        </button>
      </figcaption>

      <Modal isOpen={zoomed} onClose={() => setZoomed(false)} title="The pairing screen" size="xl">
        <img src={src} alt={alt} className="w-full rounded-lg" />
      </Modal>
    </figure>
  );
}
